import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { HelpCircle } from 'lucide-react';

const faqs = [
  {
    question: 'What is included in the notes?',
    answer: 'The notes include all 13 chapters of Class 10 CBSE Science - Physics, Chemistry, and Biology. Each chapter contains short explanations, important formulas, diagrams, and quick revision summaries.',
  },
  {
    question: 'How will I receive the notes after payment?',
    answer: 'After successful payment, you will get instant access to all notes in your dashboard. You can view them online or download PDFs directly from your account.',
  },
  {
    question: 'Are these notes updated for 2025-2026 board exams?',
    answer: 'Yes! These notes are fully updated according to the latest CBSE syllabus for the 2025-2026 academic session and board examinations.',
  },
  {
    question: 'What payment methods are accepted?',
    answer: 'We accept all major payment methods including UPI, Debit/Credit Cards, Net Banking, and popular wallets like Paytm and PhonePe through our secure Cashfree payment gateway.',
  },
  {
    question: 'Can I get a refund if I am not satisfied?',
    answer: 'Due to the digital nature of the product, we do not offer refunds once the notes are accessed. However, you can preview sample pages before purchasing to ensure the quality meets your expectations.',
  },
  {
    question: 'How long will I have access to the notes?',
    answer: 'Once purchased, you have lifetime access to the notes. You can download and keep them forever, even after your exams are over.',
  },
  {
    question: 'Are the notes suitable for all boards or only CBSE?',
    answer: 'These notes are specifically designed for CBSE Class 10 Science. However, since most state boards follow similar syllabus patterns, students from other boards may also find them helpful.',
  },
  {
    question: 'How can I contact support if I have issues?',
    answer: 'You can reach us anytime via WhatsApp by clicking the chat button on this page. Our support team typically responds within a few hours.',
  },
];

const FAQSection: React.FC = () => {
  return (
    <section 
      id="faq" 
      aria-labelledby="faq-heading"
      className="py-12 md:py-16"
    >
      <div className="container px-4 md:px-6">
        <div className="mx-auto max-w-3xl">
          {/* Header */}
          <header className="mb-10 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary-dark">
              <HelpCircle className="h-4 w-4" />
              Got Questions?
            </div>
            <h2 
              id="faq-heading"
              className="mb-4 text-2xl font-bold tracking-tight text-foreground md:text-3xl"
            >
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground">
              Everything you need to know about our Class 10 Science notes
            </p>
          </header>

          {/* FAQ Accordion */}
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="rounded-xl border border-border/50 bg-card px-5 shadow-sm transition-all hover:border-primary/30"
              >
                <AccordionTrigger className="py-4 text-left font-semibold text-foreground hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA */}
          <div className="mt-10 text-center">
            <p className="text-muted-foreground">
              Still have questions?{' '}
              <a 
                href="https://wa.me/917022174299?text=Hi,%20I%20have%20a%20question%20about%20Class%2010%20Science%20notes"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary-dark hover:underline"
              >
                Chat with us on WhatsApp
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
